import type { QueryClient } from '@tanstack/react-query'
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router'
import { useMe, useStatus } from '@api/generated'
import { authDisabled } from '@auth/keycloak'
import { ConnectCard } from '@components/ConnectCard'
import Header from '@components/Header'
import { PageSpinner } from '@components/PageSpinner'
import { errorMessage } from '@utils/errors'
import TanStackQueryProvider from '../integrations/tanstack-query/root-provider'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
})

/** Shows the page once the backend is reachable and a Q-Park account is connected. */
function Gate() {
  const me = useMe()
  const status = useStatus()
  if (me.isPending || status.isPending) return <PageSpinner />
  if (me.isError || status.isError) {
    return (
      <div role="alert" className="alert alert-error mx-4 sm:mx-8">
        Das Backend ist nicht erreichbar:{' '}
        {errorMessage(me.error ?? status.error)}
      </div>
    )
  }
  if (!status.data.connected) {
    return (
      <div className="px-4 sm:px-8 max-w-md mx-auto">
        {me.data.admin ? (
          <ConnectCard />
        ) : (
          <div role="alert" className="alert alert-warning">
            Der Parkplatz ist noch nicht eingerichtet. Bitte einen
            Administrator, das Q-Park-Konto zu verbinden.
          </div>
        )}
      </div>
    )
  }
  return <Outlet />
}

function RootComponent() {
  return (
    <TanStackQueryProvider>
      <div className="app-shell font-sans antialiased max-w-[1720px] mx-auto flex flex-col min-h-dvh">
        {authDisabled && (
          <div
            role="alert"
            className="alert alert-error rounded-none justify-center text-sm font-bold"
          >
            Authentifizierung ist deaktiviert (Entwicklungsmodus): Jeder, der
            diesen Server erreicht, ist Administrator.
          </div>
        )}
        <Header />
        <div className="py-4 sm:py-8 flex-1">
          <Gate />
        </div>
      </div>
    </TanStackQueryProvider>
  )
}
